import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { ParcelLookup } from "./parcel-types";

export type { LngLatRing, ParcelLookup, ParcelMatch } from "./parcel-types";

const PointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

/**
 * Returns the official parcel boundary for a point when one exists, otherwise
 * a clearly-flagged approximate result with no geometry.
 */
export const lookupParcel = createServerFn({ method: "POST" })
  .validator((input: unknown) => PointSchema.parse(input))
  .handler(async ({ data }): Promise<ParcelLookup> => {
    const { requireParcelSearchBudget } = await import("./public-rate-limit.server");
    await requireParcelSearchBudget("parcel", { charge: false });
    const { resolveParcel } = await import("./parcel-lookup.server");
    return resolveParcel(data.lat, data.lng);
  });

/**
 * Address-first parcel lookup: assessor parcel number (APN/PIN) match first,
 * spatial point-in-polygon second, with an ambiguous-boundary flag when the
 * rooftop point sits within 20 ft of a lot line.
 */
export const lookupParcelForAddress = createServerFn({ method: "POST" })
  .validator((input: unknown) =>
    PointSchema.extend({ address: z.string().min(3).max(300) }).parse(input),
  )
  .handler(async ({ data }): Promise<ParcelLookup> => {
    const { requireParcelSearchBudget } = await import("./public-rate-limit.server");
    await requireParcelSearchBudget("parcel", { charge: false });
    const { resolveParcelForAddress } = await import("./parcel-lookup.server");
    return resolveParcelForAddress(data.address, data.lat, data.lng);
  });

/** Result of a manual assessor parcel number (APN / PIN) search. */
export interface ApnSearchDto {
  parcel: ParcelLookup;
  center: { lat: number; lng: number };
}

/**
 * Manual APN / PIN search: matches the assessor identifier directly instead of
 * geocoding a street address.
 */
export const searchByApn = createServerFn({ method: "POST" })
  .validator((input: unknown) =>
    z
      .object({
        apn: z.string().min(3).max(60),
        state: z
          .string()
          .length(2)
          .transform((s) => s.toUpperCase())
          .optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<ApnSearchDto | null> => {
    const { requireParcelSearchBudget } = await import("./public-rate-limit.server");
    await requireParcelSearchBudget("parcel", { charge: false });
    const { searchParcelByApn } = await import("./apn-search.server");
    return searchParcelByApn(data.apn, data.state ?? null);
  });

export interface ParcelCandidateDto extends ParcelLookup {
  id: string;
  areaSqm: number;
  distanceMeters: number;
  contains: boolean;
}

/**
 * Nearby published lots around the point, for the manual parcel picker.
 */
export const listParcelCandidates = createServerFn({ method: "POST" })
  .validator((input: unknown) =>
    PointSchema.extend({ radiusMeters: z.number().min(25).max(1000).optional() }).parse(input),
  )
  .handler(async ({ data }): Promise<ParcelCandidateDto[]> => {
    const { requireParcelSearchBudget } = await import("./public-rate-limit.server");
    // Part of a search the caller already paid for: verify, don't re-charge.
    await requireParcelSearchBudget("parcel", { charge: false });
    const { findParcelCandidates } = await import("./parcel-lookup.server");
    return findParcelCandidates(data.lat, data.lng, data.radiusMeters ?? 120);
  });

export type { ParcelSearchCandidate, ParcelSearchResult } from "./parcel-search.server";

/**
 * Four-stage search: exact APN/address match, free geocode, point-in-lot,
 * then up to 5 "did you mean" lots from the county parcel records.
 */
export const searchParcels = createServerFn({ method: "POST" })
  .validator((input: unknown) =>
    z
      .object({
        query: z.string().trim().min(3).max(300),
        state: z.string().length(2).optional(),
        countyFips: z.string().regex(/^\d{5}$/).optional(),
        county: z.string().max(100).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireParcelSearchBudget } = await import("./public-rate-limit.server");
    await requireParcelSearchBudget("parcel", { charge: false });
    const { resolveParcelSearch } = await import("./parcel-search.server");
    return resolveParcelSearch(data);
  });
